import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, ShoppingBag, Droplet, Sun, Edit, ShoppingCart, Trash2, FileChartColumn, Plus, HeartCrackIcon, X } from "lucide-react";
import { authAPI } from "../../services/api";
import cacheService from "../../services/cacheService";

export default function ProduceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [produce, setProduce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    const fetchProduceData = async () => {
      try {
        if (!produce) {
          setLoading(true);
        }

        // Try to get from cache first for immediate display
        const cachedResult = await cacheService.getCachedResponse(`/view/my_produce/${id}`, 'GET');
        if (cachedResult && cachedResult.status && cachedResult.data && !produce) {
          const data = cachedResult.data;
          const formattedProduce = formatProduceData(data, id);
          setProduce(formattedProduce);
          setLoading(false);
        }

        const result = await authAPI.getProduceView(id);

        if (result.status) {
          const data = result.data;
          const formattedProduce = formatProduceData(data, id);
          setProduce(formattedProduce);
        }
      } catch (error) {
        console.error('Error fetching produce data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduceData();
    }
  }, [id]);

  const formatProduceData = (data, produceId) => {
    return {
      id: produceId,
      name: data.name,
      image: data.image,
      category: data.category,
      plantingDate: data.planting_date === "null" ? "Not planted yet" : data.planting_date,
      harvestDate: data.harvest_date === "null" ? "Not harvested yet" : data.harvest_date,
      wateringSchedule: data.care_instructions?.[0]?.watering_schedule || "Not specified",
      sunlightNeeds: data.care_instructions?.[0]?.sunlight_frequency || "Not specified",
      fertilizingSchedule: data.care_instructions?.[0]?.fertilizing_frequency || "Not specified",
      careHistory: data.care_activities || [],
      notes: "No additional notes available."
    };
  };

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const result = await authAPI.deleteFarmProduce(id);

      showToast(result.status ? 'success' : 'error', result.message);

      if (result.status) {
        // Navigate back to produce list after successful deletion
        setTimeout(() => {
          navigate('/farmer-dashboard/produce');
        }, 1500);
      }
    } catch (error) {
      console.error('Error deleting produce:', error);
      showToast('error', 'An error occurred while deleting the produce.');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="produce-view-loading">
        Loading produce details...
      </div>
    );
  }

  if (!produce) {
    return (
      <div className="produce-view-error">
        <h2>Produce not found</h2>
        <p>The requested produce item could not be found.</p>
        <Link to="/farmer-dashboard/produce" className="back-to-produce-btn">
          <ArrowLeft size={16} /> Back to My Produce
        </Link>
      </div>
    );
  }

  return (
    <div className="produce-view-container">
      <div className="produce-view-header">
        <Link to="/farmer-dashboard/produce" className="back-to-produce-btn">
          <ArrowLeft size={16} /> Back
        </Link>
        <div className="produce-view-actions">
          <button
            className="produce-view-delete-btn"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={16} /> Delete
          </button>
        </div>
      </div>

      <div className="produce-view-content">
        <div className="produce-view-main">
          <div className="produce-view-image-container">
            <img src={produce.image} alt={produce.name} className="produce-view-image" />

          </div>

          <div className="produce-view-info">
            <h1 className="produce-view-title">{produce.name}</h1>
            <div className="produce-view-meta">
              <div className="produce-view-meta-item">
                <span className="produce-view-meta-label">Category:</span>
                <span className="produce-view-meta-value">{produce.category}</span>
              </div>

              <div className="produce-view-meta-item">
                <span className="produce-view-meta-label">Harvest Date:</span>
                <span className="produce-view-meta-value">{produce.harvestDate}</span>
              </div>
            </div>

            <div className="produce-view-buttons">
              <Link to={`/farmer-dashboard/myfarm/manage/${produce.id}`} className="produce-view-manage-btn">
                <ShoppingBag size={16} /> Manage farm produce
              </Link >
              <Link to={`/farmer-dashboard/product/${produce.id}/care`} className="produce-view-sell-btn">
                <HeartCrackIcon size={16} /> Care
              </Link>
            </div>
          </div>
        </div>

        <div className="produce-view-tabs">
          <button
            className={`produce-view-tab ${activeTab === "details" ? "produce-view-tab-active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          <button
            className={`produce-view-tab ${activeTab === "care" ? "produce-view-tab-active" : ""}`}
            onClick={() => setActiveTab("care")}
          >
            Care Instructions
          </button>
          <button
            className={`produce-view-tab ${activeTab === "history" ? "produce-view-tab-active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            Care History
          </button>
          <button
            className={`produce-view-tab ${activeTab === "notes" ? "produce-view-tab-active" : ""}`}
            onClick={() => setActiveTab("notes")}
          >
            Notes
          </button>
        </div>

        <div className="produce-view-tab-content">
          {activeTab === "details" && (
            <div className="produce-view-details">
              <div className="produce-view-details-row">
                <div className="produce-view-details-item">
                  <div className="produce-view-details-icon">
                    <Calendar size={20} />
                  </div>
                  <div className="produce-view-details-text">
                    <h4>Planting Date</h4>
                    <p>{produce.plantingDate}</p>
                  </div>
                </div>
                <div className="produce-view-details-item">
                  <div className="produce-view-details-icon">
                    <Calendar size={20} />
                  </div>
                  <div className="produce-view-details-text">
                    <h4>Harvest Date</h4>
                    <p>{produce.harvestDate}</p>
                  </div>
                </div>
              </div>

            </div>
          )}

          {activeTab === "care" && (
            <div className="produce-view-care">
              <div className="produce-view-care-item">
                <div className="produce-view-care-icon">
                  <Droplet size={20} />
                </div>
                <div className="produce-view-care-text">
                  <h4>Watering Schedule</h4>
                  <p>{produce.wateringSchedule}</p>
                </div>
              </div>
              <div className="produce-view-care-item">
                <div className="produce-view-care-icon">
                  <Sun size={20} />
                </div>
                <div className="produce-view-care-text">
                  <h4>Sunlight Needs</h4>
                  <p>{produce.sunlightNeeds}</p>
                </div>
              </div>
              <div className="produce-view-care-item">
                <div className="produce-view-care-icon">
                  <FileChartColumn size={20} />
                </div>
                <div className="produce-view-care-text">
                  <h4>Fertilizing Schedule</h4>
                  <p>{produce.fertilizingSchedule}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "history" && (
            <div className="produce-view-history">
              {produce.careHistory.length > 0 ? (
                <table className="produce-view-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>Cost</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produce.careHistory.map((entry, index) => (
                      <tr key={index}>
                        <td>{entry.date}</td>
                        <td>{entry.action}</td>
                        <td>${entry.cost}</td>
                        <td>{entry.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-care-history">
                  <p>No care activities recorded yet.</p>
                </div>
              )}
              <button className="produce-view-add-history-btn">
                <Plus size={16} /> Add Care Action
              </button>
            </div>
          )}

          {activeTab === "notes" && (
            <div className="produce-view-notes">
              <div className="produce-view-notes-content">
                <p>{produce.notes}</p>
              </div>{/* 
              <button className="produce-view-edit-notes-btn">
                <Edit size={16} /> Edit Notes
              </button> */}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
            <button
              className="toast-close-fxk"
              onClick={() => setToast({ show: false, type: '', message: '' })}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay-fxk">
          <div className="delete-modal-fxk">
            <div className="delete-modal-header-fxk">
              <h2>Confirm Delete</h2>
              <button
                className="delete-modal-close-fxk"
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="delete-modal-body-fxk">
              <p>Are you sure you want to delete "{produce?.name}" from your farm?</p>
              <p className="text-danger">This action cannot be undone.</p>
            </div>
            <div className="delete-modal-footer-fxk">
              <button
                className="delete-modal-cancel-fxk"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="delete-modal-confirm-fxk"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #10b981;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          padding: 16px;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-size: 18px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message-fxk {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
        }

        .toast-close-fxk {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .toast-close-fxk:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .delete-modal-overlay-fxk {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .delete-modal-fxk {
          background: white;
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .delete-modal-header-fxk {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .delete-modal-header-fxk h2 {
          margin: 0;
          color: #1f2937;
          font-size: 20px;
          font-weight: 600;
        }

        .delete-modal-close-fxk {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .delete-modal-close-fxk:hover {
          background: #f3f4f6;
        }

        .delete-modal-body-fxk {
          margin-bottom: 24px;
        }

        .delete-modal-body-fxk p {
          margin: 0 0 8px 0;
          color: #6b7280;
          line-height: 1.5;
        }

        .delete-modal-footer-fxk {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .delete-modal-cancel-fxk {
          padding: 10px 20px;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .delete-modal-cancel-fxk:hover:not(:disabled) {
          background: #f9fafb;
        }

        .delete-modal-confirm-fxk {
          padding: 10px 20px;
          border: none;
          background: #ef4444;
          color: white;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .delete-modal-confirm-fxk:hover:not(:disabled) {
          background: #dc2626;
        }

        .delete-modal-cancel-fxk:disabled,
        .delete-modal-confirm-fxk:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}